'use client';

import React, { useEffect, useState, useRef } from 'react';
import styles from './scss/segmentation.module.scss';
import { getLycaSegementationBasedOnCustomer } from '../../apiservice/aiLycaPilot';

/**
 * TypingText
 * Renders text letter-by-letter, then calls onDone
 */
const TypingText = ({
  text,
  onDone,
  speed = 50,
}: {
  text: string;
  onDone?: () => void;
  speed?: number;
}) => {
  const [displayedText, setDisplayedText] = useState('');

  useEffect(() => {
    let i = 0;
    setDisplayedText('');
    const interval = setInterval(() => {
      setDisplayedText(text.slice(0, i + 1));
      i++;
      if (i === text.length) {
        clearInterval(interval);
        onDone?.();
      }
    }, speed);

    return () => clearInterval(interval);
  }, [text, speed, onDone]);

  return <>{displayedText}</>;
};

const SegMent2 = ({ onComplete }: { onComplete?: () => void }) => {
  const phaseOrder = ['title', 'desc', 'chart', 'maindesc', 'rev1', 'rev2', 'avg1', 'avg2'];
  const [segmentdata, setSegmentData] = useState<any>([]);
  const [visibleSection, setVisibleSection] = useState(0);
  const [phase, setPhase] = useState(phaseOrder[0]);
  const chartRefs = useRef<(HTMLDivElement | null)[]>([]);
  const fieldRefs = useRef<Record<string, HTMLDivElement | null>>({});

  useEffect(() => {
    const fetchData = async () => {
      const body = { fromYear: "2024", toYear: "2025", domainId: " " };
      try {
        const res = await getLycaSegementationBasedOnCustomer(body);
        if (res?.data?.statusCode === 200) {
          setSegmentData(res.data.response);
        }
      } catch (error) {
        console.error('Error fetching segmentation target data:', error);
      }
    };



    fetchData(); // ✅ call it
  }, []);

  const clusterDescriptions: Record<number, string> = {
    0: "Heavy data usage but almost no voice, low balance, and small top-ups – typical “smartphone-only” prepaid behavior",
    1: "Extremely light users with almost no spend or engagement",
    2: "Balanced data + voice usage and some installment behavior; mid-spend segment",
    3: "Consume large amounts of both data and voice; maintain healthy balances; willing to pay for premium usage",
    4: "Keep large prepaid balances, under-utilize bundles, buy more than they use – strong “roll-over” candidates",
    5: "Similar to Heavy data-voice power users but with higher bundle spend and balances – premium subscribers",
    6: "High spenders with instalments, likely family or business plans; prefer smoothing payments"
  };

  const clusterHeading: Record<number, string> = {
    0: "DataLite ",
    1: "IdleSaver",
    2: "EverydayFlex ",
    3: "MaxStream ",
    4: "BundleBank ",
    5: "EliteEdge ",
    6: "PrimePay "
  };

  const carouselItems = segmentdata.map((item: any) => {
    return {
      count: item.TotalCustomers.toLocaleString(),
      countTitle: "Customers",
      heading: `${clusterHeading[item.Cluster] || "Segment information not available"}`,
      headingDesc: clusterDescriptions[item.Cluster] || "Segment information not available.",
      avgRevenueCount: `${Number(item.avgerage_revenue).toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      })} Kr`,
      clvCount: `${Number(item.avgerage_revenue).toLocaleString(undefined, {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      })} Kr`
    };
  });


  console.log('carouselItems ==', carouselItems);

  // const carouselItems.map((data)=>{
  //   sections = [
  //     {
  //       title: '3,38,419',
  //       desc: 'Active Customers',
  //       chart: 'Microcopy',
  //       maindesc: 'Microcopy for Current Complaints',
  //       rev1: 'Avg Revenue',
  //       rev2: '£5400',
  //       avg1: 'CLV',
  //       avg2: '£300',
  //     },
  //     {
  //       title: '2,11,932',
  //       desc: 'Churn Risk Users',
  //       chart: 'Analysis',
  //       maindesc: 'Users showing churn signals',
  //       rev1: 'Potential Loss',
  //       rev2: '£4200',
  //       avg1: 'Risk Score',
  //       avg2: '75%',
  //     },
  //     {
  //       title: '2,11,932',
  //       desc: 'Churn Risk Users',
  //       chart: 'Analysis',
  //       maindesc: 'Users showing churn signals',
  //       rev1: 'Potential Loss',
  //       rev2: '£4200',
  //       avg1: 'Risk Score',
  //       avg2: '75%',
  //     },
  //     {
  //       title: '2,11,932',
  //       desc: 'Churn Risk Users',
  //       chart: 'Analysis',
  //       maindesc: 'Users showing churn signals',
  //       rev1: 'Potential Loss',
  //       rev2: '£4200',
  //       avg1: 'Risk Score',
  //       avg2: '75%',
  //     },
  //   ];
  // })

  console.log("carouselItems ==", carouselItems);

  const sections = carouselItems.map((data: any) => ({
    title: data.count,
    desc: data.countTitle,
    chart: data.heading,
    maindesc: data.headingDesc,
    rev1: "Avg Revenue",
    rev2: `${data.avgRevenueCount}`,
    avg1: "CLV",
    avg2: `${data.clvCount}`,
  }));

  console.log("sections ==", sections);


  // Scroll when section changes
  useEffect(() => {
    chartRefs.current[visibleSection]?.scrollIntoView({
      behavior: 'smooth',
      block: 'center',
    });
  }, [visibleSection]);

  // Scroll when phase changes
  useEffect(() => {
    const key = `${visibleSection}-${phase}`;
    fieldRefs.current[key]?.scrollIntoView({
      behavior: 'smooth',
      block: 'center',
    });
  }, [phase, visibleSection]);

  useEffect(() => {
    setPhase(phaseOrder[0]);
  }, [visibleSection]);

  const onTypingDone = () => {
    const currentIndex = phaseOrder.indexOf(phase);
    if (currentIndex < phaseOrder.length - 1) {
      setPhase(phaseOrder[currentIndex + 1]);
    } else {
      if (visibleSection < sections.length - 1) {
        setVisibleSection((prev) => prev + 1);
      } else {
        onComplete?.();
      }
    }
  };

  const renderField = (
    section: (typeof sections)[0],
    field: keyof typeof section,
    fieldPhase: string,
    sectionIndex: number
  ) => {
    const isCurrent = sectionIndex === visibleSection;
    const currentPhaseIndex = phaseOrder.indexOf(phase);
    const refKey = `${sectionIndex}-${fieldPhase}`;

    if (isCurrent) {
      if (phase === fieldPhase) {
        return (
          <div ref={(el) => (fieldRefs.current[refKey] = el)}>
            <TypingText text={section[field]} onDone={onTypingDone} />
          </div>
        );
      }
      if (currentPhaseIndex > phaseOrder.indexOf(fieldPhase)) {
        return section[field];
      }
      return ''; // Not yet started
    }

    return section[field]; // Past sections → show instantly
  };

  return (
    <div className={styles.cardssecsegt}>
      <div className={styles.titlearea}>
        <h4 className={styles.cdsheadertxt}>Segments</h4>
        <p className={styles.cdsdesc}>Segment customers to personalize interactions and drive results</p>
      </div>
      
      <div className={styles.cardsec}>
        {sections.map((section, i) => {
          if (i > visibleSection) return null;

          return (
            <div
              key={i}
              className={styles.cardwrapper}
              ref={(el) => (chartRefs.current[i] = el)}
            >
              <div className={styles.cdtop}>
                <h4>{renderField(section, 'title', 'title', i)}</h4>
                <p>{renderField(section, 'desc', 'desc', i)}</p>
              </div>

              <div className={styles.cddatas}>
                <div className={styles.cdtitledesc}>
                  <h4>{renderField(section, 'chart', 'chart', i)}</h4>
                  <p>{renderField(section, 'maindesc', 'maindesc', i)}</p>
                </div>
                <div className={styles.cdresults}>
                  <div className={styles.cdresblock}>
                    <h4>{renderField(section, 'rev1', 'rev1', i)}</h4>
                    <p>{renderField(section, 'rev2', 'rev2', i)}</p>
                  </div>
                  <div className={styles.cdresblock}>
                    <h4>{renderField(section, 'avg1', 'avg1', i)}</h4>
                    <p>{renderField(section, 'avg2', 'avg2', i)}</p>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default SegMent2;
